import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { Form, Formik, FormikProps, FormikValues } from "formik";
import { isArray } from "lodash";
import { FC, Fragment, useEffect, useMemo, useRef } from "react";

import DatepickerFormikMUI from "web/Layout/Datepicker/datepickerFormikMUI";
import {
  BIRTH_DATE_MAX,
  BIRTH_DATE_MIN,
} from "web/Layout/SubscriptionReceivers/SubscriptionReceiverForm/ActiveForm/constants";
import __ from "web/Layout/Translations";

import CheckboxMUI from "web/Components/Common/Form/Checkbox/checkboxMUI";
import FieldMUI from "web/Components/Common/Form/Field/fieldMUI";
import PhoneWithPrefixFormMUI from "web/Components/Common/Form/PhoneWithPrefixForm/phoneWithPrefixFormMUI";
import RequirePeselFormMUI from "web/Components/Common/Form/RequirePeselForm/requirePeselFormMUI";
import SelectFormMUI from "web/Components/Common/Form/Select/selectFormMUI";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import prepareActiveFormData from "web/utils/page/product/subscription/forms/prepareActiveFormData";
import { peselMask } from "web/utils/peselMask";
import { validateFields } from "web/utils/system/formValidator/validation";

import {
  ContentTypeActiveFormData,
  HeaderTypeActiveFormData,
  IActiveFormValues,
  ISubscriptionFormData,
  ISubscriptionReceiverFormField,
  ISubscriptionReceiverFormValues,
  PhoneWithPrefixField,
  SubscriptionActiveFormData,
  SubscriptionActiveFormDataType,
  SubscriptionActiveFormField,
  SubscriptionActiveFormFieldType,
} from "web/types/Subscription";

interface IActiveFormProps {
  configuration: SubscriptionActiveFormData[];
  fields: ISubscriptionReceiverFormField[];
  formData: ISubscriptionFormData;
  lang?: {
    key: string;
    code: string;
  };
  nameFieldsDisabled: boolean;
  submitHandler: (values: ISubscriptionReceiverFormValues) => void;
  closeHandler: () => void;
  isMobile: boolean;
  onDirtyChange: (isDirty: boolean) => void;
}

const ActiveFormMUI: FC<IActiveFormProps> = ({
  configuration,
  fields,
  formData,
  lang = { key: "pl_PL", code: "pl" },
  nameFieldsDisabled,
  submitHandler,
  closeHandler,
  isMobile,
  onDirtyChange,
}) => {
  if (!isArray(fields) || !configuration) return null;

  const ref = useRef<FormikProps<FormikValues>>(null);

  const buttonsText =
    lang.key === "pl_PL" ? __("Zapisz i zamknij") : "Save and close";
  const selectPlaceholder =
    lang.key === "pl_PL" ? __("Wybierz opcję") : "Select option";

  const getHeader = (header: HeaderTypeActiveFormData) => {
    return (
      <Typography variant="subtitle1" key={header.id} mb={1}>
        {header.value}
      </Typography>
    );
  };

  const getContent = (content: ContentTypeActiveFormData) => {
    if (!content.content) {
      return null;
    }

    const contentElement = (
      <Box
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content.content),
        }}
        sx={{
          m: 0,
          "& > p": {
            m: 0,
          },
        }}
      />
    );
    return (
      <Box key={content.id} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>
          {content.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          {content?.useCheckbox ? (
            <CheckboxMUI name={content.id} dataT1={content.name}>
              {contentElement}
            </CheckboxMUI>
          ) : (
            contentElement
          )}
        </Box>
      </Box>
    );
  };

  const getField = (field: SubscriptionActiveFormField) => {
    let input;
    let initialValuesPlaceholder;

    switch (field.type) {
      case SubscriptionActiveFormFieldType.REQUIRE_PESEL:
        input = (
          <RequirePeselFormMUI
            field={field}
            tooltipMessage={field.tooltip}
            lang={lang}
            isMobile={isMobile}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.PHONE_WITH_PREFIX:
        input = (
          <PhoneWithPrefixFormMUI
            field={field as PhoneWithPrefixField}
            lang={lang}
            isMobile={isMobile}
            tooltipMessage={field.tooltip}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.SELECT:
        initialValuesPlaceholder =
          field?.options?.find(
            (el) => el.value === (initialValues as IActiveFormValues)[field.id]
          )?.label || (initialValues as IActiveFormValues)[field.id];
        input = (
          <SelectFormMUI
            label={field.label}
            name={field.id}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            placeholder={initialValuesPlaceholder || selectPlaceholder}
            options={field.options.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            dataT1={field.label}
            sx={{ mt: 2, mb: 0 }}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.CHECKBOX:
        input = (
          <CheckboxMUI
            name={field.id}
            tooltipMessage={field.tooltip}
            dataT1={field.label}
          >
            {field.label}
          </CheckboxMUI>
        );
        break;
      case SubscriptionActiveFormFieldType.BIRTHDATE:
      case SubscriptionActiveFormFieldType.DATE: {
        const isBirthDate =
          field.type === SubscriptionActiveFormFieldType.BIRTHDATE;

        const minDateValue = isBirthDate ? BIRTH_DATE_MIN : null;
        const maxDateValue = isBirthDate ? BIRTH_DATE_MAX : null;

        input = (
          <DatepickerFormikMUI
            name={field.id}
            label={field.label}
            placeholder="DD/MM/YYYY"
            minDate={minDateValue}
            maxDate={maxDateValue}
            dataT1={field.label}
            isMobile={isMobile}
            sx={{ mt: 2, mb: 0, maxWidth: "512px" }}
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.POSTCODE:
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            placeholder="_ _ - _ _ _"
            isDataT1NotChanged
          />
        );
        break;
      case SubscriptionActiveFormFieldType.FIRSTNAME:
      case SubscriptionActiveFormFieldType.LASTNAME: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            disabled={nameFieldsDisabled}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.PESEL: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            mask={peselMask}
            dataT1={field.label}
            placeholder="- - - - - - - - - - -"
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.NUMBER: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
            enableNumericHandler
          />
        );
        break;
      }
      default: {
        input = (
          <FieldMUI
            label={field.label}
            name={field.id}
            type={field.type}
            dataT1={field.label}
            sx={{ mb: 0, maxWidth: "512px" }}
            isDataT1NotChanged
          />
        );
      }
    }

    const doesFieldHaveTooltip = field.tooltip?.trim();
    return (
      <Fragment key={field.id}>
        {doesFieldHaveTooltip ? (
          <Tooltip title={field.tooltip} placement="right">
            {input}
          </Tooltip>
        ) : (
          input
        )}
      </Fragment>
    );
  };

  const [initialValues, fieldsToValidate] = useMemo(
    () => prepareActiveFormData(fields, formData, lang),
    [fields]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const validate = validateFields(fieldsToValidate);

  const formBody = useMemo(() => {
    if (!isArrayHasItems(configuration)) return null;

    return configuration.map((section) => {
      switch (section.type) {
        case SubscriptionActiveFormDataType.HEADER:
          return getHeader(section);
        case SubscriptionActiveFormDataType.CONTENT:
          return getContent(section);
        case SubscriptionActiveFormDataType.FORM:
          return (
            <Fragment key={section.id}>
              {section.fields.map((key) => getField(key))}
              {/* <div className={styles.separator} /> */}
              <Divider sx={{ my: 3 }} />
            </Fragment>
          );
        default:
          return null;
      }
    });
  }, [configuration]);

  return (
    <Formik
      enableReinitialize
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialValues={initialValues}
      onSubmit={submitHandler}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      validate={validate}
      innerRef={ref}
    >
      {({ dirty }) => {
        useEffect(() => {
          onDirtyChange(dirty);
        }, [dirty]);

        return (
          <Form noValidate>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                ...(isMobile && {
                  px: 2,
                  pt: 3,
                  backgroundColor: "#fff",
                  borderTopRightRadius: "16px",
                  borderTopLeftRadius: "16px",
                  boxShadow:
                    "0px 1px 1px #F2F2F2, 0px 4px 24px rgba(166, 166, 166, 0.2)",
                }),
              }}
            >
              {formBody}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                ...(isMobile && {
                  background: "#fff",
                  pb: 3,
                  px: 2,
                  pt: 1,
                  flexDirection: "column",
                }),
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={closeHandler}
                sx={{
                  mr: isMobile ? 0 : 1,
                  ...(isMobile && { order: 1 }),
                }}
              >
                {__("Anuluj")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ ...(isMobile && { mb: 2 }) }}
              >
                {buttonsText}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ActiveFormMUI;
